import { useState, useEffect, useCallback } from 'react';
import { Music, Plus, Trash2, Edit2, X, Check, ExternalLink, ChevronRight, Lock, Globe, GripVertical } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_STYLES = {
  spotify: { name: 'Spotify', color: '#1DB954' },
  apple_music: { name: 'Apple Music', color: '#FA243C' },
  tidal: { name: 'TIDAL', color: '#00FFFF' },
  soundcloud: { name: 'SoundCloud', color: '#FF5500' },
  youtube: { name: 'YouTube', color: '#FF0000' }
};

export const MyPlaylistsTab = ({ user }) => {
  const [playlists, setPlaylists] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName] = useState('');

  const fetchPlaylists = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/playlists`);
      setPlaylists(response.data.playlists);
      setLimits(response.data.limits);
    } catch (error) {
      toast.error('Failed to load playlists');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlaylistTracks = useCallback(async (playlistId) => {
    try {
      const response = await axios.get(`${API}/playlists/${playlistId}/full`);
      setPlaylistTracks(response.data.track_details || []);
    } catch (error) {
      toast.error('Failed to load tracks');
    }
  }, []);

  useEffect(() => {
    fetchPlaylists();
  }, [fetchPlaylists]);

  useEffect(() => {
    if (selectedPlaylist) {
      fetchPlaylistTracks(selectedPlaylist.id);
    }
  }, [selectedPlaylist, fetchPlaylistTracks]);

  const handleCreatePlaylist = async () => {
    if (!createName.trim()) return;
    
    try {
      const response = await axios.post(`${API}/playlists`, { name: createName });
      setPlaylists([...playlists, response.data]);
      setCreateName('');
      setShowCreateForm(false);
      toast.success('Playlist created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create playlist');
    }
  };

  const handleRenamePlaylist = async (playlistId) => {
    if (!newPlaylistName.trim()) return;
    
    try {
      await axios.put(`${API}/playlists/${playlistId}`, { name: newPlaylistName });
      setPlaylists(playlists.map(p => 
        p.id === playlistId ? { ...p, name: newPlaylistName } : p
      ));
      if (selectedPlaylist?.id === playlistId) {
        setSelectedPlaylist({ ...selectedPlaylist, name: newPlaylistName });
      }
      setEditingPlaylist(null);
      setNewPlaylistName('');
      toast.success('Playlist renamed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename playlist');
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    if (!window.confirm('Are you sure you want to delete this playlist?')) return;
    
    try {
      await axios.delete(`${API}/playlists/${playlistId}`);
      setPlaylists(playlists.filter(p => p.id !== playlistId));
      if (selectedPlaylist?.id === playlistId) {
        setSelectedPlaylist(null);
        setPlaylistTracks([]);
      }
      toast.success('Playlist deleted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete playlist');
    }
  };

  const handleRemoveTrack = async (postId) => {
    if (!selectedPlaylist) return;
    
    try {
      await axios.delete(`${API}/playlists/${selectedPlaylist.id}/tracks/${postId}`);
      setPlaylistTracks(playlistTracks.filter(t => t.id !== postId));
      setSelectedPlaylist({
        ...selectedPlaylist,
        track_count: selectedPlaylist.track_count - 1,
        tracks: selectedPlaylist.tracks.filter(t => t !== postId)
      });
      setPlaylists(playlists.map(p => 
        p.id === selectedPlaylist.id 
          ? { ...p, track_count: p.track_count - 1 } 
          : p
      ));
      toast.success('Track removed!');
    } catch (error) {
      toast.error('Failed to remove track');
    }
  };

  const handleTogglePublic = async (playlistId, currentPublic) => {
    try {
      await axios.put(`${API}/playlists/${playlistId}`, { is_public: !currentPublic });
      setPlaylists(playlists.map(p => 
        p.id === playlistId ? { ...p, is_public: !currentPublic } : p
      ));
      if (selectedPlaylist?.id === playlistId) {
        setSelectedPlaylist({ ...selectedPlaylist, is_public: !currentPublic });
      }
      toast.success(currentPublic ? 'Playlist is now private' : 'Playlist is now public');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update playlist');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-playlists-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Music size={24} style={{ color: '#D4AF37' }} />
            My Playlists
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            {playlists.length}/{limits?.max_playlists} playlists | {limits?.max_tracks_per_playlist} tracks per playlist
          </p>
        </div>
        
        {playlists.length < (limits?.max_playlists || 2) && (
          <Button
            onClick={() => setShowCreateForm(true)}
            className="rounded-full"
            style={{ background: '#D4AF37', color: '#000' }}
          >
            <Plus size={16} className="mr-1" />
            New Playlist
          </Button>
        )}
      </div>

      {/* Create Playlist Form */}
      {showCreateForm && (
        <div 
          className="p-4 rounded-xl"
          style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)' }}
        >
          <div className="flex gap-2">
            <Input
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="Playlist name"
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && handleCreatePlaylist()}
            />
            <Button onClick={handleCreatePlaylist} disabled={!createName.trim()}>
              <Check size={16} />
            </Button>
            <Button variant="outline" onClick={() => { setShowCreateForm(false); setCreateName(''); }}>
              <X size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Playlists Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {playlists.map((playlist) => (
          <div
            key={playlist.id}
            className={`p-4 rounded-xl cursor-pointer transition-all ${
              selectedPlaylist?.id === playlist.id 
                ? 'ring-2 ring-[#D4AF37]' 
                : 'hover:bg-white/5'
            }`}
            style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)' }}
            onClick={() => setSelectedPlaylist(playlist)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {editingPlaylist === playlist.id ? (
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Input
                      value={newPlaylistName}
                      onChange={(e) => setNewPlaylistName(e.target.value)}
                      className="text-sm"
                      autoFocus
                    />
                    <button onClick={() => handleRenamePlaylist(playlist.id)} className="p-1">
                      <Check size={14} style={{ color: '#22c55e' }} />
                    </button>
                    <button onClick={() => { setEditingPlaylist(null); setNewPlaylistName(''); }} className="p-1">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{playlist.name}</h3>
                    {playlist.is_default && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37]">
                        Default
                      </span>
                    )}
                    {playlist.is_public ? (
                      <Globe size={14} className="text-green-500" />
                    ) : (
                      <Lock size={14} className="text-gray-400" />
                    )}
                  </div>
                )}
                
                <p className="text-sm text-gray-400 mt-1">
                  {playlist.track_count || 0}/{limits?.max_tracks_per_playlist} tracks
                </p>
              </div>
              
              {/* Actions */}
              {!playlist.is_default && editingPlaylist !== playlist.id && (
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button 
                    onClick={() => { setEditingPlaylist(playlist.id); setNewPlaylistName(playlist.name); }}
                    className="p-1.5 rounded hover:bg-white/10"
                  >
                    <Edit2 size={14} />
                  </button>
                  {limits?.can_make_public && (
                    <button 
                      onClick={() => handleTogglePublic(playlist.id, playlist.is_public)}
                      className="p-1.5 rounded hover:bg-white/10"
                      title={playlist.is_public ? 'Make Private' : 'Make Public'}
                    >
                      {playlist.is_public ? <Lock size={14} /> : <Globe size={14} />}
                    </button>
                  )}
                  <button 
                    onClick={() => handleDeletePlaylist(playlist.id)}
                    className="p-1.5 rounded hover:bg-white/10 text-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
              
              <ChevronRight size={16} className="ml-2 text-gray-400" />
            </div>
          </div>
        ))}
      </div>

      {/* Selected Playlist Tracks */}
      {selectedPlaylist && (
        <div 
          className="mt-6 p-4 rounded-xl"
          style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.3)' }}
        >
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Music size={20} style={{ color: '#D4AF37' }} />
            {selectedPlaylist.name}
            <span className="text-sm font-normal text-gray-400">
              ({playlistTracks.length} tracks)
            </span>
          </h3>
          
          {playlistTracks.length === 0 ? (
            <p className="text-center text-gray-400 py-8">
              No tracks yet. Save tracks from the Track Discovery feed!
            </p>
          ) : (
            <div className="space-y-2">
              {playlistTracks.map((track, index) => (
                <div
                  key={track.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 group"
                >
                  <span className="text-sm text-gray-500 w-6">{index + 1}</span>
                  
                  {/* Artwork */}
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-white/10 flex items-center justify-center">
                    {track.track_artwork ? (
                      <img src={track.track_artwork} alt={track.track_title} className="w-full h-full object-cover" />
                    ) : (
                      <Music size={20} className="text-gray-400" />
                    )}
                  </div>
                  
                  {/* Track Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{track.track_title || 'Unknown Track'}</p>
                    <p className="text-sm text-gray-400 truncate">{track.track_artist || 'Unknown Artist'}</p>
                  </div>
                  
                  {/* Platform */}
                  <span 
                    className="text-xs px-2 py-1 rounded-full"
                    style={{ 
                      background: `${PLATFORM_STYLES[track.track_platform]?.color || '#666'}20`,
                      color: PLATFORM_STYLES[track.track_platform]?.color || '#666'
                    }}
                  >
                    {PLATFORM_STYLES[track.track_platform]?.name || track.track_platform}
                  </span>
                  
                  {/* Posted by */}
                  <span className="text-xs text-gray-500">
                    by {track.user_name}
                  </span>
                  
                  {/* Actions */}
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <a
                      href={track.track_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded hover:bg-white/10"
                      title="Open in platform"
                    >
                      <ExternalLink size={14} style={{ color: '#D4AF37' }} />
                    </a>
                    <button
                      onClick={() => handleRemoveTrack(track.id)}
                      className="p-2 rounded hover:bg-white/10 text-red-400"
                      title="Remove from playlist"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
